import type { Mode, Op } from "../content/modes";
import type { PracticeSettings } from "../save/schema";
import { masteryWeight, type MasteryMap, type MissedMap } from "./mastery";
import { opsForMode } from "./modes";
import { questionFromFact, sameQuestion, type Question } from "./questions/question";
import { pickWeighted, sampleQuestion } from "./questions/sampler";
import { questionType, typesFor, type QuestionType } from "./questions/types";
import type { Rng } from "./rng";
import { nextTrial, TOP_RANK_INDEX, trialFor } from "./trials";

/*
  The adaptive question source that Practice and Battles share: the child's
  rank band mix, weighted towards weak types, with missed facts replayed now
  and then, within the parent's ranges. Trials never use it (brief, 3.3).
*/

/** Whether a type fits the parent's "adding up to" and "tables up to" settings. */
export function withinRange(type: QuestionType, p: PracticeSettings): boolean {
  return type.op === "add" || type.op === "sub" ? type.limit <= p.addMax : type.limit <= p.tableMax;
}

/**
 * The types to draw from: the strands in play, within the parent's ranges.
 * If the ranges rule out everything in a strand, that strand keeps its
 * gentlest types rather than disappearing.
 */
export function adaptiveTypes(ops: readonly Op[], p: PracticeSettings): QuestionType[] {
  return ops.flatMap((op) => {
    const all = typesFor([op]);
    const fit = all.filter((t) => withinRange(t, p));
    if (fit.length) return fit;
    const smallest = Math.min(...all.map((t) => t.limit));
    return all.filter((t) => t.limit === smallest);
  });
}

export interface AdaptiveContext {
  mode: Mode;
  /** The rank held in this mode; the band is the next trial's tier mix. */
  rank: number;
  ops: Record<Op, boolean>;
  practice: PracticeSettings;
  mastery: MasteryMap;
  missed: MissedMap;
  rng: Rng;
}

export interface AdaptiveOptions {
  /** Chance that a question replays a missed fact, when there is one to replay. */
  replayChance: number;
  /** How hard weak types are favoured: 1 is Practice's full strength, 0 is not at all. */
  strength: number;
}

export function adaptiveSource(ctx: AdaptiveContext, opts: AdaptiveOptions) {
  const types = adaptiveTypes(opsForMode(ctx.mode, ctx.ops), ctx.practice);
  const mix = (nextTrial(ctx.mode, ctx.rank) ?? trialFor(ctx.mode, TOP_RANK_INDEX)!).mix;
  const weight = masteryWeight(ctx.mastery, opts.strength);
  const replayable = Object.entries(ctx.missed).filter(([, m]) =>
    types.some((t) => t.id === m.type),
  );

  return (previous: Question | null): Question => {
    if (replayable.length && ctx.rng.next() < opts.replayChance) {
      const [key, m] = pickWeighted(ctx.rng, replayable, ([, x]) => x.n);
      const type = questionType(m.type)!;
      const q = questionFromFact(key, {
        typeId: type.id,
        op: type.op,
        tier: type.tier,
        points: type.points,
      });
      if (q && !sameQuestion(previous, q)) return q;
    }
    return sampleQuestion({ types, mix, rng: ctx.rng, previous, weight });
  };
}
